import { useState, useCallback, useEffect } from 'react';
import { useNotificationBar } from 'context/notificationContext';
import { PillBannerConfig, UserState, EligibilityRules, CompletionConditions, RedirectType } from 'interfaces/widget-types';
import { useUploadFiles } from 'hooks/upload';
import { getImageSrc, getRelativeIconPath } from 'services/util';
import { Upload } from 'lucide-react';
import { InformationCircleIcon, DocumentIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ToggleField } from 'components/styledFormElements/ToggleField';
import { SingleSelect } from 'components/styledFormElements/SingleSelect';
import { useAddSettings, useSettingsData, useUpdateSettings } from 'hooks/settings';

interface Props {}

const userStateOptions: { value: UserState; label: string }[] = [
    { value: 'free_user', label: 'Free User' },
    { value: 'paid_user', label: 'Paid User' },
    { value: 'trial_user', label: 'Trial User' },
    { value: 'trial_expired_user', label: 'Trial Expired User' },
];

const completionRuleOptions = [
    { value: 'fixed_date', label: 'Fixed Date/Time' },
    { value: 'conversion_completion', label: 'Conversion Completion' },
    { value: 'manual_disable', label: 'Manual Disable' },
];

const redirectOptions = [
    { value: 'EPISODE', label: 'Episode' },
    { value: 'SHOW', label: 'Show' },
    { value: 'COLLECTION', label: 'Collection' },
    { value: 'CATEGORY', label: 'Category' },
    { value: 'OTHER', label: 'Other' },
];

const PillBannerSettings = () => {
    const notification = useNotificationBar();
    const uploadMutation = useUploadFiles();
    const { mutate: addSettings, isLoading: addLoading } = useAddSettings();
    const { mutate: updateSettings, isLoading: updateLoading } = useUpdateSettings();
    const { data: settingsData, refetch } = useSettingsData();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const hasExistingData = Array.isArray(settingsData?.data) && settingsData.data.length > 0;
    const existingSetting = hasExistingData
        ? settingsData.data.find((item: any) => item.slug === 'pill_banner_setting')
        : null;
    
    // Get array of banners from settings
    const banners: any[] = existingSetting?.settings?.banners || [];
    
    // UI state
    const [showForm, setShowForm] = useState(false);
    const [editingBannerId, setEditingBannerId] = useState<string | null>(null);

    console.log('Settings Data:', settingsData);
    console.log('Existing Setting:', existingSetting);
    console.log('Banners:', banners);

    const [bannerName, setBannerName] = useState('');
    const [enabled, setEnabled] = useState(false);
    const [redirectType, setRedirectType] = useState<RedirectType>('Episode' as RedirectType);
    const [redirectId, setRedirectId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [creativeFile, setCreativeFile] = useState<File | null>(null);
    const [creativePreview, setCreativePreview] = useState<string | null>(null);
    const [userStates, setUserStates] = useState<UserState[]>([]);
    const [contentLabels, setContentLabels] = useState<string[]>([]);
    const [contentLabelsInput, setContentLabelsInput] = useState('');
    const [freeEpisodes, setFreeEpisodes] = useState(0);
    const [completionRules, setCompletionRules] = useState<string[]>([]);
    const [fixedDateTime, setFixedDateTime] = useState('');
    const [priority, setPriority] = useState(1);
    const [dismissible, setDismissible] = useState(false);

    // Load banner data when editing
    useEffect(() => {
        if (editingBannerId && banners.length > 0) {
            const banner = banners.find((b: any) => b.id === editingBannerId);
            if (banner) {
                setBannerName(banner.bannerName || '');
                setEnabled(banner.enabled ?? false);
                setRedirectType(banner.redirection_to || 'Episode');
                setRedirectId(banner.redirection_id || '');
                setStartDate(banner.scheduler?.startDate || '');
                setEndDate(banner.scheduler?.endDate || '');
                setUserStates(banner.eligibilityRules?.userState || []);
                setContentLabels(banner.eligibilityRules?.userBehavior?.hasWatchedContentWithLabels || []);
                setContentLabelsInput((banner.eligibilityRules?.userBehavior?.hasWatchedContentWithLabels || []).join(', '));
                setFreeEpisodes(banner.eligibilityRules?.userBehavior?.hasCompletedFreeEpisodes || 0);
                setPriority(banner.priority || 1);
                setDismissible(banner.dismissible ?? false);
                setFixedDateTime(banner.completionConditions?.fixedDateTime || '');
                
                const completionConditionsKeys = Object.keys(banner.completionConditions || {});
                const rules = completionConditionsKeys.filter(key => {
                    const value = banner.completionConditions?.[key];
                    return value === true || (typeof value === 'string' && value.length > 0);
                }).map(key => {
                    if (key === 'fixedDateTime') return 'fixed_date';
                    if (key === 'conversionCompletion') return 'conversion_completion';
                    if (key === 'manualDisable') return 'manual_disable';
                    return key;
                });
                setCompletionRules(rules);
                
                // Load creative preview
                if (banner.thumbnail) {
                    const imageUrl = typeof banner.thumbnail === 'string' ? banner.thumbnail : '';
                    const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : getImageSrc(imageUrl);
                    setCreativePreview(fullImageUrl);
                }
            }
        }
    }, [editingBannerId, banners]);


    useEffect(() => {
        return () => {
            if (creativePreview && creativePreview.startsWith('blob:')) {
                URL.revokeObjectURL(creativePreview);
            }
        };
    }, [creativePreview]);

    const handleCreativeFileSelect = useCallback((file: File | null) => {
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                notification.push({
                    message: 'File size must be under 5MB',
                    type: 'error',
                });
                return;
            }
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                notification.push({
                    message: 'Only JPG, JPEG, PNG, WebP allowed',
                    type: 'error',
                });
                return;
            }

            setCreativeFile(file);
            const previewUrl = URL.createObjectURL(file);
            setCreativePreview(previewUrl);
        } else {
            setCreativeFile(null);
            setCreativePreview(null);
        }
    }, [notification]);

    const handleCreativeFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        handleCreativeFileSelect(file);
    }, [handleCreativeFileSelect]);

    const handleEnabledChange = useCallback((checked: boolean) => {
        setEnabled(checked);
    }, []);

    const handleRedirectTypeChange = useCallback((option: { value: string; label: string }) => {
        setRedirectType(option.value as RedirectType);
    }, []);

    const handleContentLabelsInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setContentLabelsInput(value);

        const labels = value.split(/[\s,，,]+/).map(s => s.trim()).filter(Boolean);
        setContentLabels(labels);
    };

    const resetForm = () => {
        setBannerName('');
        setEnabled(false);
        setRedirectType('Episode' as RedirectType);
        setRedirectId('');
        setStartDate('');
        setEndDate('');
        setCreativeFile(null);
        setCreativePreview(null);
        setUserStates([]);
        setContentLabels([]);
        setContentLabelsInput('');
        setFreeEpisodes(0);
        setCompletionRules([]);
        setFixedDateTime('');
        setPriority(1);
        setDismissible(false);
        setEditingBannerId(null);
    };

    const handleCreateNew = () => {
        resetForm();
        setShowForm(true);
    };

    const handleEdit = (bannerId: string) => {
        setEditingBannerId(bannerId);
        setShowForm(true);
    };

    const handleDelete = async (bannerId: string) => {
        if (!window.confirm('Are you sure you want to delete this banner?')) {
            return;
        }

        const updatedBanners = banners.filter((b: any) => b.id !== bannerId);
        const formData = {
            slug: 'pill_banner_setting',
            settings: {
                banners: updatedBanners,
            },
            status: 1,
            is_delete: 0
        };

        if (existingSetting?.id) {
            updateSettings(
                {
                    data: formData,
                    setting_id: existingSetting.id,
                },
                {
                    onSuccess: () => {
                        notification.push({
                            title: 'Success',
                            type: 'success',
                            message: 'Pill Banner deleted successfully',
                        });
                        refetch();
                    },
                    onError: () => {
                        notification.push({
                            title: 'Error',
                            type: 'error',
                            message: 'Failed to delete Pill Banner',
                        });
                    },
                }
            );
        }
    };

    const handleCancel = () => {
        resetForm();
        setShowForm(false);
    };

    const handleSave = async () => {
        setIsSubmitting(true);

        // Validate max 3 active banners
        const activeBanners = banners.filter((b: any) => b.enabled && b.id !== editingBannerId);
        if (enabled && activeBanners.length >= 3) {
            notification.push({
                message: 'Maximum 3 active pill banners allowed',
                type: 'error',
            });
            setIsSubmitting(false);
            return;
        }

        if (!bannerName || (!redirectId && redirectType !== 'Other')) {
            notification.push({
                message: 'Please fill all required fields',
                type: 'error',
            });
            setIsSubmitting(false);
            return;
        }

        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);

            if (end <= start) {
                notification.push({
                    message: 'End date must be after start date',
                    type: 'error',
                });
                setIsSubmitting(false);
                return;
            }
        }

        if (redirectType === 'Other' && redirectId) {
            const urlRegex = /^(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\/\w#!:.?+=&%@!\-]))?$/;
            if (!urlRegex.test(redirectId)) {
                notification.push({
                    message: 'Invalid URL format',
                    type: 'error',
                });
                setIsSubmitting(false);
                return;
            }
        }

        let uploadedCreativeUrl: string | null = null;

        if (creativeFile instanceof File) {
            try {
                const formData = new FormData();
                formData.append('file', creativeFile);
                formData.append('customPath', 'settings/pill-banner');

                const res = await uploadMutation.mutateAsync({
                    data: formData,
                });
                uploadedCreativeUrl = res?.data?.data?.key || '';
            } catch (error) {
                notification.push({
                    message: 'Failed to upload creative image',
                    type: 'error',
                });
                setIsSubmitting(false);
                return;
            }
        } else if (editingBannerId && banners.length > 0) {
            const banner = banners.find((b: any) => b.id === editingBannerId);
            if (banner?.thumbnail && typeof banner.thumbnail === 'string') {
                uploadedCreativeUrl = getRelativeIconPath(banner.thumbnail);
            }
        }

        const eligibilityRules: EligibilityRules = {};
        if (userStates.length > 0) eligibilityRules.userState = userStates;
        if (contentLabels.length > 0 || freeEpisodes > 0) {
            eligibilityRules.userBehavior = {};
            if (contentLabels.length > 0) eligibilityRules.userBehavior.hasWatchedContentWithLabels = contentLabels;
            if (freeEpisodes > 0) eligibilityRules.userBehavior.hasCompletedFreeEpisodes = freeEpisodes;
        }
        
        const completionConditions: CompletionConditions = {};
        completionRules.forEach(rule => {
            if (rule === 'fixed_date') completionConditions.fixedDateTime = fixedDateTime;
            if (rule === 'conversion_completion') completionConditions.conversionCompletion = true;
            if (rule === 'manual_disable') completionConditions.manualDisable = true;
        });

        // Create banner object
        const newBanner = {
            id: editingBannerId || `banner_${Date.now()}`,
            bannerName,
            enabled,
            redirection_to: redirectType,
            redirection_id: redirectId,
            bannerType: 'pill',
            thumbnail: uploadedCreativeUrl,
            scheduler: (startDate || endDate) ? { startDate, endDate } : null,
            eligibilityRules: Object.keys(eligibilityRules).length > 0 ? eligibilityRules : null,
            completionConditions: Object.keys(completionConditions).length > 0 ? completionConditions : null,
            priority,
            dismissible,
        };

        // Update banners array
        let updatedBanners;
        if (editingBannerId) {
            // Update existing banner
            updatedBanners = banners.map((b: any) => b.id === editingBannerId ? newBanner : b);
        } else {
            // Add new banner
            updatedBanners = [...banners, newBanner];
        }

        const formData = {
            slug: 'pill_banner_setting',
            settings: {
                banners: updatedBanners,
            },
            status: 1,
            is_delete: 0
        };

        if (existingSetting?.id) {
            updateSettings(
                {
                    data: formData,
                    setting_id: existingSetting.id,
                },
                {
                    onSuccess: () => {
                        notification.push({
                            title: 'Success',
                            type: 'success',
                            message: editingBannerId ? 'Pill Banner updated successfully' : 'Pill Banner created successfully',
                        });
                        refetch();
                        setIsSubmitting(false);
                        resetForm();
                        setShowForm(false);
                    },
                    onError: (error: any) => {
                        notification.push({
                            title: 'Error',
                            type: 'error',
                            message: 'Failed to update Pill Banner settings',
                        });
                        setIsSubmitting(false);
                    },
                }
            );
        } else {
            addSettings(
                { data: formData },
                {
                    onSuccess: () => {
                        notification.push({
                            title: 'Success',
                            type: 'success',
                            message: 'Pill Banner created successfully',
                        });
                        refetch();
                        setIsSubmitting(false);
                        resetForm();
                        setShowForm(false);
                    },
                    onError: (error: any) => {
                        notification.push({
                            title: 'Error',
                            type: 'error',
                            message: 'Failed to create Pill Banner settings',
                        });
                        setIsSubmitting(false);
                    },
                }
            );
        }
    };

    return (
        <div className="w-full px-6 pt-6 bg-white dark:text-gray-200 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md">
            <aside className="flex flex-auto items-center justify-between">
                <div className="flex items-center gap-2">
                    <DocumentIcon className="h-6 w-6" />
                    <h4 className="text-xl">Pill Banner Settings</h4>
                </div>
                {!showForm && (
                    <button
                        onClick={handleCreateNew}
                        disabled={banners.filter(b => b.enabled).length >= 3}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Create New Banner
                    </button>
                )}
            </aside>

            {!showForm ? (
                // List View
                <div className="my-4">
                    <div className="mb-4 flex items-center justify-between">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {banners.filter(b => b.enabled).length} / 3 active banners ({banners.length} total configured)
                        </p>
                    </div>
                    
                    {banners.length === 0 ? (
                        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-12 text-center">
                            <DocumentIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Pill Banners</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">Get started by creating your first pill banner</p>
                            <button
                                onClick={handleCreateNew}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                <PlusIcon className="h-5 w-5" />
                                Create New Banner
                            </button>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                                <thead className="bg-gray-50 dark:bg-slate-900">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Banner Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Redirect</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                                    {banners.map((banner: any) => (
                                        <tr key={banner.id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">{banner.bannerName}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${banner.enabled ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                                                    {banner.enabled ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {banner.priority}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                {banner.redirection_to}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => handleEdit(banner.id)}
                                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                                                >
                                                    <PencilIcon className="h-5 w-5 inline" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(banner.id)}
                                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                >
                                                    <TrashIcon className="h-5 w-5 inline" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ) : (
                // Form View
                <div className="my-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {editingBannerId ? 'Edit' : 'Create'} Pill Banner
                            </h3>
                            <button
                                onClick={handleCancel}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                ✕
                            </button>
                        </div>

                        <h4 className="text-md font-medium text-gray-900 dark:text-white">Basic Configuration</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Banner Name *</label>
                        <input
                            value={bannerName}
                            onChange={(e) => setBannerName(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="Enter banner name"
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Enabled
                        </label>
                        <ToggleField
                            checked={enabled}
                            onChange={(e) => handleEnabledChange(e.target.checked)}
                        />

                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Redirect Type *</label>
                        <SingleSelect
                            value={redirectType}
                            options={redirectOptions}
                            placeholder="Select Redirect Type"
                            onChange={handleRedirectTypeChange}
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {redirectType === 'Other' ? 'URL *' : 'Redirect ID *'}
                        </label>
                        <input
                            type={redirectType === 'Other' ? 'url' : 'text'}
                            value={redirectId}
                            onChange={(e) => setRedirectId(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder={redirectType === 'Other' ? 'https://example.com' : 'Enter redirect ID'}
                        />
                    </div>
                </div>

                <div className='flex flex-col gap-1'>
                    <div className="flex items-center gap-1 mb-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Creative Upload
                        </label>
                        <div className="relative group flex items-center">
                            <InformationCircleIcon className="w-4 h-4 text-slate-400 hover:text-blue-600 cursor-pointer" />
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                                <p className="font-semibold mb-1">Image requirements</p>
                                <ul className="space-y-1">
                                    <li className="flex gap-1">
                                        <span>•</span>
                                        <span>Banner creative image for marketing campaigns</span>
                                    </li>
                                    <li className="flex gap-1">
                                        <span>•</span>
                                        <span>Formats: JPG, PNG, WebP</span>
                                    </li>
                                    <li className="flex gap-1">
                                        <span>•</span>
                                        <span>File size: Max 5MB</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="border-2 border-dashed rounded-lg p-3 hover:border-blue-400 transition">
                        <input
                            id="thumbnail"
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            className="hidden"
                            onChange={handleCreativeFileChange}
                        />
                        <label htmlFor="thumbnail" className="flex flex-col items-center justify-center cursor-pointer">
                            {creativePreview ? (
                                <img
                                    src={creativePreview}
                                    alt="Creative preview"
                                    className="w-full h-32 object-cover rounded border"
                                />
                            ) : (
                                <>
                                    <Upload
                                        className="text-gray-400 mb-2"
                                        size={25}
                                    />
                                    <p className="text-sm text-gray-500">
                                        Click to upload creative
                                    </p>
                                    <p className="text-xs text-gray-400">
                                        JPG / PNG / WebP · Max 5MB
                                    </p>
                                </>
                            )}
                        </label>
                    </div>
                </div>
            </div>

            <div className="space-y-4 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Start Date</label>
                        <input
                            type="datetime-local"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">End Date</label>
                        <input
                            type="datetime-local"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                        />
                    </div>
                </div>
            </div>

            <div className="space-y-4 mt-6">
                <div className='flex flex-col gap-1'>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">User State</label>
                    <div className="space-y-2">
                        {userStateOptions.map((option) => (
                            <div key={option.value} className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id={`user-state-${option.value}`}
                                    checked={userStates.includes(option.value)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setUserStates([...userStates, option.value]);
                                        } else {
                                            setUserStates(userStates.filter(state => state !== option.value));
                                        }
                                    }}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor={`user-state-${option.value}`} className="text-sm text-gray-700 dark:text-gray-300">
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Content Labels</label>
                        <input
                            value={contentLabelsInput}
                            onChange={handleContentLabelsInputChange}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="drama, comedy, action"
                        />
                    </div>

                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Free Episodes Completed</label>
                        <input
                            type="number"
                            value={freeEpisodes}
                            onChange={(e) => setFreeEpisodes(Number(e.target.value))}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="0"
                        />
                    </div>
                </div>
            </div>

            <div className="space-y-4 mt-6">
                <div className='flex flex-col gap-1'>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Completion Rules</label>
                    <div className="space-y-2">
                        {completionRuleOptions.map((option) => (
                            <div key={option.value} className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id={`completion-${option.value}`}
                                    checked={completionRules.includes(option.value)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setCompletionRules([...completionRules, option.value]);
                                        } else {
                                            setCompletionRules(completionRules.filter(rule => rule !== option.value));
                                        }
                                    }}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor={`completion-${option.value}`} className="text-sm text-gray-700 dark:text-gray-300">
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {completionRules.includes('fixed_date') && (
                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Fixed Date/Time</label>
                        <input
                            type="datetime-local"
                            value={fixedDateTime}
                            onChange={(e) => setFixedDateTime(e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                        />
                    </div>
                )}
            </div>

            <div className="space-y-4 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className='flex flex-col gap-1'>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Priority</label>
                        <input
                            type="number"
                            value={priority}
                            onChange={(e) => setPriority(Number(e.target.value))}
                            min="1"
                            max="3"
                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500"
                            placeholder="1"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Used for ordering when multiple banners are eligible (1-3, lower number = higher priority)</p>
                    </div>

                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="dismissible"
                            checked={dismissible}
                            onChange={(e) => setDismissible(e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="dismissible" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Dismissible (X button)
                        </label>
                    </div>
                </div>
            </div>

                <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-slate-600">
                    <button
                        onClick={handleCancel}
                        className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSubmitting || updateLoading || addLoading}
                        className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting || updateLoading || addLoading ? 'Saving...' : (editingBannerId ? 'Update Banner' : 'Create Banner')}
                    </button>
                </div>
                </div>
            )}
        </div>
    );
};

export default PillBannerSettings;
